<template>
  <div>
    <h3>Staff members</h3>

    <div
      v-if="isTableLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>

    <template v-else>
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group>
          <b-button
            variant="primary"
            class="mx-1 float-right"
            size="sm"
            @click="openStaffModal()"
          >
            <span>Add staff member</span>
          </b-button>
        </b-form-group>
      </div>

      <vue-good-table
        :columns="columns"
        :rows="staff"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
              :src="pictureUrl(props.row.picture)"
              class="staff-table-picture"
              alt=""
            >
          </span>
          <span
            v-else-if="props.column.field === 'name'"
            class="d-inline-flex align-items-center"
          >
            <span><DatatableUserText :value="props.row.name" /></span>
            <feather-icon
              v-if="Number(props.row.inactive) === 1"
              icon="XOctagonIcon"
              size="16"
              class="text-danger ml-50"
              v-b-tooltip.hover
              title="Blocked"
            />
          </span>
          <span
            v-else-if="props.column.field === 'action'"
            class="action-link-group staff-action"
          >
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openEditModal(props.row)">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>
    </template>

    <ManageStaff
      :click-date="clickDate"
      :staff-member="selectedStaffMember"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BPagination,
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastFailure } from '@/libs/toastification'
import { normalizeImageUrl } from '@/libs/utils'
import ManageStaff from '@/views/Components/ManageStaff.vue'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'

export default {
  components: {
    TablePagination,
    ManageStaff,
    DatatableCellValue,
    DatatableUserText,
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BSpinner,
  },
  data() {
    return {
      isTableLoading: true,
      searchTerm: '',
      clickDate: null,
      selectedStaffMember: null,
      pageLength: 25,
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          sortable: false,
          width: '62px',
          thClass: 'staff-picture-col',
          tdClass: 'staff-picture-col',
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Phone',
          field: 'phone',
        },
        {
          label: 'Email',
          field: 'email',
        },
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center staff-action-col',
          tdClass: 'text-center staff-action-col',
          sortable: false,
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      staff: 'staff/staff',
    }),
  },
  async mounted() {
    await this.loadStaff()
      .then(() => {
        this.isTableLoading = false
      })
      .catch(() => {
        toastFailure('Something went wrong.')
      })
  },
  methods: {
    ...mapActions({
      loadStaff: 'staff/loadStaff',
    }),
    pictureUrl(picture) {
      return normalizeImageUrl(picture)
    },
    openStaffModal() {
      this.selectedStaffMember = null
      this.clickDate = new Date()
    },
    openEditModal(row) {
      this.selectedStaffMember = row
      this.clickDate = new Date()
    },
  },
}
</script>

<style scoped>
.staff-picture-col {
  width: 62px !important;
  min-width: 62px !important;
  max-width: 62px !important;
  padding-left: 0.35rem !important;
  padding-right: 0.35rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

.staff-table-picture {
  display: inline-block;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  vertical-align: middle;
}

.staff-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.staff-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.staff-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
